(() => {
  const roots = new Set();
  const registry = new WeakMap();
  let globalListenersBound = false;

  class RPHeaderSearchController {
    constructor(root) {
      this.root = root;
      this.details = root.querySelector('details');
      this.summary = root.querySelector('summary');
      this.input = root.querySelector('input[type="search"]');
      this.overlay = root.querySelector('.modal-overlay');
      this.closeButton = root.querySelector('.search-modal__close-button');
      this.form = root.querySelector('form[role="search"]');
      this.abortController = new AbortController();
      this.bind();
    }

    get signal() {
      return this.abortController.signal;
    }

    bind() {
      this.details?.addEventListener('toggle', () => this.syncState(), { signal: this.signal });
      this.summary?.addEventListener('click', () => window.setTimeout(() => this.syncState(true), 0), { signal: this.signal });
      this.overlay?.addEventListener('click', (event) => {
        event.preventDefault();
        this.close(true);
      }, { signal: this.signal });
      this.closeButton?.addEventListener('click', (event) => {
        event.preventDefault();
        this.close(true);
      }, { signal: this.signal });
      this.form?.addEventListener('submit', () => this.close(false), { signal: this.signal });
      this.syncState();
    }

    isOpen() {
      return Boolean(this.details?.hasAttribute('open'));
    }

    syncState(focusInput = false) {
      if (!this.summary) return;
      const isOpen = this.isOpen();
      this.summary.setAttribute('aria-expanded', String(isOpen));
      this.root.toggleAttribute('data-search-open', isOpen);
      if (isOpen) window.RPHeaderController?.revealHeader?.();
      if (isOpen && focusInput) {
        window.requestAnimationFrame(() => this.input?.focus({ preventScroll: true }));
      }
    }

    close(restoreFocus = false) {
      if (!this.details || !this.isOpen()) {
        this.syncState();
        return;
      }

      if (typeof this.root.close === 'function') {
        this.root.close(restoreFocus);
      } else {
        this.details.removeAttribute('open');
        document.body.classList.remove('overflow-hidden');
        if (typeof window.removeTrapFocus === 'function') {
          window.removeTrapFocus(restoreFocus ? this.summary : null);
        }
        if (restoreFocus) {
          window.requestAnimationFrame(() => this.summary?.focus({ preventScroll: true }));
        }
      }

      this.syncState();
    }

    destroy() {
      this.close(false);
      this.abortController.abort();
      this.root.removeAttribute('data-rp-header-search-bound');
      this.root.removeAttribute('data-search-open');
    }
  }

  function getController(root) {
    return registry.get(root);
  }

  function openControllers() {
    return Array.from(roots).map(getController).filter((controller) => controller?.isOpen());
  }

  function closeAll(restoreFocus = false, exceptRoot = null) {
    openControllers().forEach((controller) => {
      if (controller.root !== exceptRoot) controller.close(restoreFocus);
    });
  }

  function handleDocumentClick(event) {
    openControllers().forEach((controller) => {
      if (!controller.root.contains(event.target)) controller.close(false);
    });
  }

  function handleDocumentKeydown(event) {
    if (event.key !== 'Escape') return;
    const controllers = openControllers();
    if (!controllers.length) return;
    event.preventDefault();
    controllers.forEach((controller) => controller.close(true));
  }

  function handlePageShow() {
    closeAll(false);
  }

  function ensureGlobalListeners() {
    if (globalListenersBound || roots.size === 0) return;
    document.addEventListener('click', handleDocumentClick, true);
    document.addEventListener('keydown', handleDocumentKeydown);
    window.addEventListener('pageshow', handlePageShow);
    globalListenersBound = true;
  }

  function removeGlobalListeners() {
    if (!globalListenersBound || roots.size > 0) return;
    document.removeEventListener('click', handleDocumentClick, true);
    document.removeEventListener('keydown', handleDocumentKeydown);
    window.removeEventListener('pageshow', handlePageShow);
    globalListenersBound = false;
  }

  function findRoots(scope = document) {
    return scope.matches?.('[data-rp-header-search]')
      ? [scope]
      : Array.from(scope.querySelectorAll?.('[data-rp-header-search]') || []);
  }

  function init(scope = document) {
    findRoots(scope).forEach((root) => {
      if (registry.has(root)) return;
      root.setAttribute('data-rp-header-search-bound', 'true');
      registry.set(root, new RPHeaderSearchController(root));
      roots.add(root);
    });
    ensureGlobalListeners();
  }

  function destroy(scope = document) {
    findRoots(scope).forEach((root) => {
      const controller = registry.get(root);
      if (!controller) return;
      controller.destroy();
      registry.delete(root);
      roots.delete(root);
    });
    removeGlobalListeners();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
  window.RPHeaderSearch = { init, destroy, closeAll };
})();